import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const BlogAdmin = ({ darkMode, showNotification }) => {
  const [blogs, setBlogs] = useState([]);
  const [formData, setFormData] = useState({ title: '', slug: '', content: '' });
  const [editingId, setEditingId] = useState(null);
  const [authorId, setAuthorId] = useState(null);
  const [authorName, setAuthorName] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const navigate = useNavigate();

  const fetchBlogs = async () => {
    try {
      const res = await fetch('/api/blogs');
      const data = await res.json();
      setBlogs(data);
    } catch (err) {
      console.error('Failed to fetch blogs:', err);
    }
  };

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) {
      navigate('/login');
      return;
    }

    const user = JSON.parse(storedUser);
    if (!user.roles.includes('admin')) {
      showNotification && showNotification('Admin access required', 'error');
      navigate('/');
      return;
    }

    setAuthorId(user._id);
    setAuthorName(user.name);
    fetchBlogs();
  }, [navigate, showNotification]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    const method = editingId ? 'PUT' : 'POST';
    const url = editingId ? `/api/blogs/${editingId}` : '/api/blogs';
    const payload = editingId ? { ...formData } : { ...formData, author: authorId };

    try {
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        setFormData({ title: '', slug: '', content: '' });
        setEditingId(null);
        fetchBlogs();
      } else {
        console.error('Error saving post:', data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleEdit = (blog) => {
    setFormData({ title: blog.title, slug: blog.slug, content: blog.content });
    setEditingId(blog._id);
  };

  const confirmDelete = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`/api/blogs/${deleteId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) fetchBlogs();
    } catch (err) {
      console.error(err);
    }
    setDeleteId(null);
  };

  return (
    <div className={`max-w-4xl mx-auto p-6 pb-24 rounded shadow-md ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-black'}`}>
      <h2 className="text-2xl font-bold mb-6">Blog Admin Panel</h2>

      <div className="mb-6">
        <h3 className="text-xl font-semibold mb-2">{editingId ? 'Edit Blog Post' : 'Create New Blog Post'}</h3>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <input
            type="text"
            name="title"
            placeholder="Title"
            value={formData.title}
            onChange={handleChange}
            className={`w-full p-2 border rounded ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`}
          />
          <input
            type="text"
            name="slug"
            placeholder="Slug"
            value={formData.slug}
            onChange={handleChange}
            className={`w-full p-2 border rounded ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`}
          />
          <textarea
            name="content"
            placeholder="Markdown Content"
            rows="6"
            value={formData.content}
            onChange={handleChange}
            className={`w-full p-2 border rounded ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`}
          ></textarea>
          <p className="text-sm italic">Author: {authorName}</p>
          <button type="submit" className="bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700">
            {editingId ? 'Update Post' : 'Save Post'}
          </button>
        </form>
      </div>

      <div>
        <h3 className="text-xl font-semibold mb-2">Existing Posts</h3>
        <ul className="space-y-2">
          {blogs.map((blog) => (
            <li key={blog._id} className="border p-2 rounded flex justify-between items-center">
              <div>
                <div className="font-bold">{blog.title}</div>
                <div className="text-sm text-gray-500">{blog.slug}</div>
                <div className="text-sm italic">by {blog.author?.name || 'Unknown'}</div>
              </div>
              <div className="space-x-2">
                <button onClick={() => handleEdit(blog)} className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600">
                  Edit
                </button>
                <button onClick={() => setDeleteId(blog._id)} className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600">
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white dark:bg-gray-800 p-6 rounded shadow-md w-96">
            <h2 className="text-xl mb-4">Confirm Deletion</h2>
            <p className="mb-4">Are you sure you want to delete this blog post?</p>
            <div className="flex justify-end space-x-2">
              <button onClick={confirmDelete} className="bg-red-500 text-white px-4 py-2 rounded">Delete</button>
              <button onClick={() => setDeleteId(null)} className="bg-gray-500 text-white px-4 py-2 rounded">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogAdmin;
